'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CornerBrackets } from '../ui/CornerBrackets';
import { RpgButton } from '../ui/RpgButton';
import {
  OVERLAY_LABELS,
  type OnboardingStep,
  type SceneId,
} from './copy';

interface OnboardingOverlayProps {
  open: boolean;
  steps: readonly OnboardingStep[];
  /**
   * Illustration in the left column (creator tour, etc.). Omitted when
   * `renderLeftColumn` is used instead.
   */
  renderStage?: (scene: SceneId) => ReactNode;
  /** User skipped (or pressed ESC) before completing. */
  onSkip: (options: { remember: boolean }) => void;
  /** User reached the last step and clicked the CTA. */
  onComplete: (options: { remember: boolean }) => void;
  /** Eyebrow shown above the title (e.g. "Player tour"). */
  eyebrow?: string;
  /** Replaces the default ✕ control when `renderProfilePreview` is not set. */
  renderHeaderClose?: () => ReactNode;
  /**
   * Identity + pedestal preview above the tile stage in the left column.
   * When set, the header uses a plain ✕ so the profile reads large on the left.
   */
  renderProfilePreview?: () => ReactNode;
  /**
   * Single left column (player tour) — e.g. live Lite board tile preview.
   * When set, `renderProfilePreview` and `renderStage` are not shown.
   */
  renderLeftColumn?: () => ReactNode;
  /**
   * Toggle the entrance / step transitions. Creator-style flows use the
   * full framer-motion animation set restored from `develop`; the
   * first-run player tour opts out so the tile preview inside
   * `renderLeftColumn` stays visually stable.
   *
   * Defaults to `true`.
   */
  animate?: boolean;
  /**
   * Optional per-step slot rendered in the copy column immediately below
   * `step.body`, for injecting interactive controls on a specific step
   * without spawning a whole new scene renderer.
   */
  renderCopyExtra?: (step: OnboardingStep) => ReactNode;
}

/**
 * Shared modal scaffolding for both PlayerOnboarding and CreatorOnboarding.
 *
 * Layout (desktop):
 *
 *   ┌──────────────────────────────── card ────────────────────────────────┐
 *   │  [eyebrow]  ●●○○○                                              [×]   │
 *   │  ┌─────────────────────────────┬──────────────────────────────────┐│
 *   │  │  Profile + plinth (opt.)    │  Title                           ││
 *   │  │  3D / 2D tile stage         │  Body copy                       ││
 *   │  └─────────────────────────────┴──────────────────────────────────┘│
 *   │  [Skip]                       [Don't show again ☑]   [Back] [Next]  │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Mobile: stage stacks above copy; footer collapses to a single row.
 *
 * ESC dismisses (treated as Skip). Focus moves to the primary CTA on mount
 * and on each step change. Tab cycling is constrained to the card.
 */
export function OnboardingOverlay({
  open,
  steps,
  renderStage,
  onSkip,
  onComplete,
  eyebrow,
  renderHeaderClose,
  renderProfilePreview,
  renderLeftColumn,
  animate = true,
  renderCopyExtra,
}: OnboardingOverlayProps) {
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  const animateEnabled = animate && !reduceMotion;

  const [stepIndex, setStepIndex] = useState(0);
  const [remember, setRemember] = useState(true);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const ctaRef = useRef<HTMLButtonElement | null>(null);

  // Reset to step 0 each time the overlay reopens (replay from help icon).
  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setRemember(true);
    }
  }, [open]);

  const safeStep = Math.min(stepIndex, steps.length - 1);
  const step = steps[safeStep];
  const isLast = safeStep === steps.length - 1;

  const advance = useCallback(() => {
    if (isLast) {
      onComplete({ remember });
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [isLast, onComplete, remember, steps.length]);

  const retreat = useCallback(() => {
    setStepIndex((i) => (i <= 0 ? 0 : i - 1));
  }, []);

  const skip = useCallback(() => {
    onSkip({ remember });
  }, [onSkip, remember]);

  // Keyboard handling: ESC = skip, ←/→ = step navigation. We don't bind
  // these on a wrapping element because the overlay is render-conditional
  // and React's onKeyDown only fires on focused descendants.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        skip();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        advance();
      } else if (event.key === 'ArrowLeft') {
        if (safeStep <= 0) return;
        event.preventDefault();
        retreat();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, skip, advance, retreat, safeStep]);

  // Focus the primary CTA whenever the step changes — gives keyboard users
  // a predictable starting point and lets Enter/Space advance.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      ctaRef.current?.focus();
    }, 30);
    return () => window.clearTimeout(id);
  }, [open, safeStep]);

  // Cheap focus trap: redirect Tab from outside the card back into it.
  useEffect(() => {
    if (!open) return;
    const onTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const card = cardRef.current;
      if (!card) return;
      const focusable = card.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onTab);
    return () => document.removeEventListener('keydown', onTab);
  }, [open]);

  // Lock body scroll while open so the lobby underneath doesn't drift.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Progress dots. `aria-current="step"` (not boolean) is what the
  // ARIA spec expects for wayfinding inside a sequence — screen
  // readers announce "current step" with that value and silence it
  // otherwise. Active/past/future states are distinguished by opacity
  // so the history reads at a glance without crowding the header.
  const dots = useMemo(
    () =>
      steps.map((s, i) => (
        <button
          key={s.id}
          type="button"
          onClick={() => setStepIndex(i)}
          aria-label={`Go to step ${i + 1} of ${steps.length}`}
          aria-current={i === safeStep ? 'step' : undefined}
          className={cn(
            'h-1 w-5 rounded-full transition-colors',
            i === safeStep
              ? 'bg-amber-300/90'
              : i < safeStep
                ? 'bg-amber-200/40 hover:bg-amber-200/60'
                : 'bg-amber-100/15 hover:bg-amber-100/25',
          )}
        />
      )),
    [steps, safeStep],
  );

  return (
    <AnimatePresence>
      {open && step && (
        <motion.div
          key="onboarding-backdrop"
          aria-hidden={false}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={cn(
            'fixed inset-0 z-[60] flex items-center justify-center',
            'bg-black/75 backdrop-blur-sm px-4 py-6',
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: animateEnabled ? 0.18 : 0 }}
        >
          <motion.div
            ref={cardRef}
            key="onboarding-card"
            initial={animateEnabled ? { opacity: 0, scale: 0.97 } : { opacity: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={animateEnabled ? { opacity: 0, scale: 0.98 } : { opacity: 0 }}
            transition={{
              duration: animateEnabled ? 0.22 : 0,
              ease: [0.2, 0.8, 0.2, 1],
            }}
            className={cn(
              // Tighter card — the previous 1180px cap made the left-hand
              // animation stage read as "ultra wide" against ~320px of
              // copy. 880px keeps the stage at a legible square-ish
              // aspect while still giving the copy column breathing room.
              'relative flex w-full max-w-[min(880px,calc(100vw-32px))] flex-col overflow-hidden',
              'max-h-[min(calc(100vh-48px),720px)]',
              'rounded-[5px] border border-amber-400/25',
              'bg-gradient-to-b from-[#120e08]/92 via-[#0c0a06]/95 to-[#0a0805]/97',
              'shadow-[0_30px_120px_-40px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,200,100,0.05)_inset]',
            )}
          >
            <CornerBrackets size={18} color="rgba(255, 200, 100, 0.65)" />

            <header
              className={cn(
                'flex items-center justify-between gap-4 border-b border-amber-400/10 px-5 py-3',
              )}
            >
              <div className="flex flex-1 items-center gap-3">
                {eyebrow ? (
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-100/45">
                    {eyebrow}
                  </span>
                ) : null}
                <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-100/35">
                  {safeStep + 1} of {steps.length}
                </span>
                <div className="flex items-center gap-1.5" role="presentation">
                  {dots}
                </div>
              </div>
              <button
                type="button"
                onClick={skip}
                aria-label="Close tour"
                className={cn(
                  '-mr-1 flex h-8 w-8 items-center justify-center rounded-[3px] transition',
                  'text-amber-100/70 hover:bg-amber-400/10 hover:text-amber-100',
                )}
              >
                {renderProfilePreview == null && renderHeaderClose ? (
                  renderHeaderClose()
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
              </button>
            </header>

            <div
              className={cn(
                'relative grid flex-1 overflow-hidden',
                // Balanced split — the 2D stage reads best near-square,
                // and the copy column needs ≥320px to avoid breaking
                // sentences awkwardly on the longer creator steps.
                'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]',
              )}
            >
              <section
                className={cn(
                  // Below `sm` (≤640px viewport — iPhone SE, mini, fold)
                  // the stage is intentionally compressed so the copy
                  // column's `ViewModePick` radios (3D vs Lite 2D) on
                  // the player tour stay visible without scrolling.
                  // 46vh of an iPhone SE's 667px viewport = 307px,
                  // which combined with the 50px header + 50px footer
                  // pushed the picker off the bottom of the 619px
                  // (= 100vh − 48px) card. 30vh / 220px keeps the
                  // stage legible while leaving ~250px for copy +
                  // picker on the smallest target screen.
                  'relative flex min-h-[min(30vh,220px)] flex-col bg-[#06050a]',
                  'sm:min-h-[min(46vh,420px)]',
                  'border-b border-amber-400/10 lg:min-h-0 lg:border-b-0 lg:border-r',
                )}
              >
                {renderLeftColumn ? (
                  <div className="relative flex flex-1 flex-col items-center justify-center px-5 py-8 sm:px-8 sm:py-10">
                    {renderLeftColumn()}
                  </div>
                ) : (
                  <>
                    {renderProfilePreview ? (
                      <div className="flex shrink-0 justify-center px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-7 lg:px-8 lg:pt-8">
                        {renderProfilePreview()}
                      </div>
                    ) : null}
                    <div
                      className={cn(
                        'relative min-h-[220px] flex-1 lg:min-h-[280px]',
                        renderProfilePreview && 'min-h-[200px] sm:min-h-[240px]',
                      )}
                    >
                      {renderStage?.(step.scene)}
                    </div>
                  </>
                )}
              </section>

              {/* Copy pane scrolls if a step's body ever exceeds the
                  card height — prevents long future translations / recap
                  variants from getting clipped below the footer. */}
              <section className="relative flex flex-col gap-4 border-t border-amber-400/10 px-6 py-6 lg:border-l lg:border-t-0 lg:px-7 lg:py-8 overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step.id}
                    initial={
                      animateEnabled ? { opacity: 0, y: 8 } : { opacity: 1 }
                    }
                    animate={{ opacity: 1, y: 0 }}
                    exit={
                      animateEnabled ? { opacity: 0, y: -6 } : { opacity: 0 }
                    }
                    transition={{
                      duration: animateEnabled ? 0.2 : 0,
                      ease: [0.2, 0.8, 0.2, 1],
                    }}
                    className="flex flex-col gap-3"
                  >
                    <h2
                      id={titleId}
                      className="font-serif text-[20px] leading-tight text-amber-50 md:text-[22px]"
                    >
                      {step.title}
                    </h2>
                    <p className="text-[13.5px] leading-relaxed text-amber-100/80 md:text-[14px]">
                      {step.body}
                    </p>
                    {renderCopyExtra ? renderCopyExtra(step) : null}
                  </motion.div>
                </AnimatePresence>
              </section>
            </div>

            <footer
              className={cn(
                'flex flex-wrap items-center justify-between gap-4 border-t border-amber-400/10 px-5 py-3.5',
              )}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={skip}
                  className={cn(
                    'font-mono text-[11px] uppercase tracking-[0.22em]',
                    'text-amber-100/45 transition hover:text-amber-100/80',
                  )}
                >
                  {OVERLAY_LABELS.skip}
                </button>
                <label className="flex cursor-pointer items-center gap-1.5 select-none">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                    className="h-3 w-3 cursor-pointer rounded-[2px] border-amber-300/40 bg-transparent accent-amber-400"
                  />
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-100/40">
                    {OVERLAY_LABELS.dontShow}
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-2">
                <RpgButton
                  tone="silver"
                  size="sm"
                  onClick={retreat}
                  disabled={safeStep === 0}
                  iconLeft={<ArrowLeft className="h-3 w-3" />}
                  silent
                >
                  {OVERLAY_LABELS.back}
                </RpgButton>
                <RpgButton
                  ref={ctaRef}
                  tone="gold"
                  size="sm"
                  onClick={advance}
                  iconRight={!isLast ? <ArrowRight className="h-3 w-3" /> : undefined}
                >
                  {isLast ? (step.cta ?? OVERLAY_LABELS.next) : OVERLAY_LABELS.next}
                </RpgButton>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
